import React, { useMemo, useState, useRef, useEffect } from 'react';
import moment from 'moment';
import { useHistory } from 'react-router-dom';
import { styled, Autocomplete, TextField, createFilterOptions } from '@mui/material';
import { Search } from '@mui/icons-material';
import { useScheduleSettings } from '../../../../../main/apps/FunctionalApps/schedule-list-view/ScheduleSettingsStore';
import useGlobalSearchByLTQuery from '../utils/queries';
import getNavigationConfig from '../configs';
import { addRecentItem, getRecentItems } from '../../ToolbarV2/components/SwitchUserButton.helpers';
import { buildGlobalSearchOptions, createListboxComponent, getSearchableNavItems, renderOption } from './NavSearch.helpers';

const NAV_SEARCH_KEY = 'nav_search_recent_paths';

const SearchField = styled(TextField)(({ theme }) => ({
    '& .MuiOutlinedInput-root': {
        height: '40px',
        fontSize: '16px',
        fontWeight: 400,
        backgroundColor: theme.palette.nav.background.light,
        color: theme.palette.nav.text.white,
        svg: {
            color: theme.palette.nav.divider.dark,
        },
        '& .MuiOutlinedInput-notchedOutline': {
            borderColor: theme.palette.nav.divider.dark,
        },
        '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: theme.palette.primary.main,
        },
    },
}));

const NavSearch = ({ user }) => {
    const config = getNavigationConfig();
    const history = useHistory();
    const inputRef = useRef(null);
    const [search, setSearch] = useState('');
    const [open, setOpen] = useState(false);
    const { setFilters, setSettings } = useScheduleSettings();

    useEffect(() => {
        const handleKeyDown = (event) => {
            // Check for Cmd+K on Mac or Ctrl+K on Windows/Linux
            if ((event.metaKey || event.ctrlKey) && event.key === 'k') {
                event.preventDefault();
                inputRef.current?.focus();
                setOpen(true);
            }
        };

        document.addEventListener('keydown', handleKeyDown);

        return () => {
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, []);

    const options = useMemo(() => getSearchableNavItems(config, user), [config, user]);

    const { data: globalSearchResults } = useGlobalSearchByLTQuery({ ltNumber: search, enabled: !!search && search.length >= 8 });
    const globalOptions = useMemo(() => buildGlobalSearchOptions(globalSearchResults, user, search), [globalSearchResults, user, search]);

    const recentItems = getRecentItems(NAV_SEARCH_KEY);
    const recentOptions = recentItems
        .map((item) => {
            if (typeof item === 'object' && item?.path) {
                return item;
            }
            return null;
        })
        .filter(Boolean);

    const showGlobal = globalOptions.length > 0;
    const showRecent = search.trim() === '' && recentOptions.length > 0;
    const displayOptions = showGlobal ? globalOptions : showRecent ? recentOptions : options;

    const ListboxComponent = useMemo(() => createListboxComponent(showRecent), [showRecent]);

    const handleSelect = (_, value, newWindow = false) => {
        const { path, props } = value;

        if (path) {
            addRecentItem(NAV_SEARCH_KEY, value);

            if (props) {
                if (props.type === 'schedule') {
                    const { ltNumber, start_date } = value.props;
                    setFilters((prev) => ({ ...prev, start: moment(start_date), search: { lt_number: ltNumber } }));
                    setSettings((prev) => ({ ...prev, selectedDay: moment(start_date) }));
                }
            }

            if (newWindow) {
                window.open(`${window.location.origin}/#${path}`, '_blank');
            } else {
                history.push(path);
            }
            inputRef.current?.blur();
        }
    };

    return (
        <div style={{ margin: '12px', marginBottom: '8px' }}>
            <Autocomplete
                options={displayOptions}
                getOptionLabel={(option) => option.title || ''}
                filterSelectedOptions
                filterOptions={(opts, state) =>
                    showGlobal || showRecent
                        ? opts // wrap
                        : createFilterOptions({ stringify: (option) => option.title || '' })(opts, state)
                }
                value={null}
                popupIcon={null}
                inputValue={search}
                onInputChange={(e, newInput) => setSearch(newInput)}
                onChange={handleSelect}
                open={open}
                onOpen={() => setOpen(true)}
                onClose={() => setOpen(false)}
                size="small"
                fullWidth
                ListboxComponent={ListboxComponent}
                renderInput={(params) => (
                    <SearchField
                        {...params}
                        inputRef={inputRef}
                        placeholder="Go to..."
                        variant="outlined"
                        InputProps={{
                            ...params.InputProps,
                            startAdornment: <Search sx={{ mx: 0.5 }} />,
                            'data-dd-privacy': 'allow',
                        }}
                    />
                )}
                renderOption={(props, option) => renderOption(props, option, handleSelect)}
            />
        </div>
    );
};

export default NavSearch;
